import { library } from '@fortawesome/fontawesome-svg-core';
import { fab } from '@fortawesome/free-brands-svg-icons';
import 'App.scss';
import { SidebarLayoutRoute } from 'Components/Navigation/SidebarLayoutRoute';
import { EntityProvider } from 'Context';
import { AddEditMain } from 'Features/AddEdit/AddEditMain';
import { AppointmentMain } from 'Features/Appointments';
import { CaseManagementMain } from 'Features/CaseManagement';
import { OpenReferralsCommercialMain } from 'Features/CaseManagement/OpenReferralsCommercialMain';
import { ViewClaimantMain } from 'Features/Claimants';
import { ClaimMain } from 'Features/Claims';
import ClientDashboard from 'Features/ClientDashboard';
import ReviewerProfile from 'v3/Components/Pages/ReviewerProfile/ReviewerProfile';
import ReviewersV3Page from 'v3/Components/Pages/Reviewers/Reviewers';
import ReviewerImport from 'v3/Components/Pages/ReviewerImport/ReviewerImport';
import { AddEditV3Role } from 'Features/AddEdit/AddEditV3Role';
import { ComplianceMain } from 'Features/Compliance/ComplianceMain';
import { ConfigurationHeader, ConfigurationMain } from 'Features/Configuration';
import { DashboardMain } from 'Features/Dashboard';
import { DocumentsMain } from 'Features/Documents/DocumentsMain';
import { ElmahError, ElmahErrors } from 'Features/Elmah/ElmahErrors';
import { Unauthorized } from 'Features/Error/Unauthorized';
import { EndSession } from 'Features/Error/EndSession';
import { ExamplesMain } from 'Features/Examples/ExamplesMain';
import { ViewGuidelineMain } from 'Features/Guidelines/ViewGuidelineMain';
import { ReportsMain } from 'Features/Invoices/ReportsMain';
import { AddAuditReport } from 'Features/QualityAssurance';
import { ReferralCommercialMain, ReferralMain } from 'Features/Referrals';
import {
  ReferralsRequestProposalsMain,
  ReferralsRequestProposaslCommercial,
} from 'Features/ReferralsRequestProposals';
import { ReferralsRequestMain } from 'Features/ReferralsRequests';
import { ReviewerConvertMain } from 'Features/ReviewerConvert';
import { Reviewers } from 'Features/Reviewers';
import { ExternalApiSwagger } from 'Features/Swagger/ExternalApiSwagger';
import { Swagger } from 'Features/Swagger/Swagger';
import {
  UserManagementHeader,
  UserManagementMain,
} from 'Features/UserManagement';
import { ViewMain } from 'Features/View';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { fullRoute, ROUTES } from 'Navigation';
import React from 'react';
import { Switch } from 'react-router-dom';
import {
  FeatureFlags,
  FlaggedLayoutRoute,
  isFeatureEnabled,
} from 'Utilities/FeatureFlagProvider';
import { SecureLayoutRouteV3 } from 'Components/Navigation';
import { PermissionCodesV3 } from 'Models/Authorization/permissionCodesV3';
import { ComponentLibrary } from 'v3/Components/ComponentLibrary';
import { LayoutRoute } from './Components/Navigation';
import { Layout } from './Features/Shared';
import { MyInvoices } from 'Features/MyInvoices';

library.add(fab);

export const App: React.FC = () => {
  return (
    <Layout>
      <Switch>
        <LayoutRoute
          path={ROUTES.COMPONENT_EXAMPLES}
          component={ComponentLibrary}
        />
        <LayoutRoute path={ROUTES.SWAGGER} component={Swagger} />
        <LayoutRoute path={ROUTES.API_SWAGGER} component={ExternalApiSwagger} />
        <LayoutRoute
          path={ROUTES.DASHBOARD}
          component={DashboardMain}
          layoutProps={{ noPadding: true }}
        />
        <LayoutRoute path={ROUTES.MY_INVOICES} component={MyInvoices} />
        <FlaggedLayoutRoute
          flag={FeatureFlags.ClientDashboard}
          path={ROUTES.CLIENT_DASHBOARD}
          component={ClientDashboard}
          layoutProps={{ noPadding: true }}
        />
        <LayoutRoute path={ROUTES.DOCUMENTS} component={DocumentsMain} />
        <SecureLayoutRouteV3
          flag={FeatureFlags.V3ReviewerProfile}
          path={ROUTES.REVIEWER_PROFILE}
          exact
          component={ReviewersV3Page}
          layoutProps={{ noPadding: true }}
          permissionsV3={[PermissionCodesV3.Reviewer.Panel.View]}
        />
        {!isFeatureEnabled(FeatureFlags.V3ReviewerProfile) && (
          <LayoutRoute path={ROUTES.REVIEWER_PANEL} component={Reviewers} />
        )}
        <SecureLayoutRouteV3
          path={ROUTES.REVIEWER_IMPORT}
          exact
          component={ReviewerImport}
          layoutProps={{ noPadding: true }}
          permissionsV3={[PermissionCodesV3.Reviewer.Profile.Import]}
        />
        <SecureLayoutRouteV3
          flag={FeatureFlags.V3ReviewerProfile}
          path={ROUTES.REVIEWER_PROFILE + '/:id/:tab?'}
          component={ReviewerProfile}
          layoutProps={{ noPadding: true }}
          permissionsV3={[PermissionCodesV3.Reviewer.Profile.View]}
        />
        <SecureLayoutRouteV3
          path={ROUTES.V3_ROLE + '/:id?'}
          exact
          component={AddEditV3Role}
          layoutProps={{ noPadding: true }}
          permissionsV3={[PermissionCodesV3.Role.Management.View]}
        />
        <LayoutRoute path={ROUTES.REFERRALS} component={CaseManagementMain} />
        <LayoutRoute path={ROUTES.REPORTS} component={ReportsMain} />
        <LayoutRoute path={ROUTES.COMPLIANCE} component={ComplianceMain} />
        <LayoutRoute
          path={ROUTES.COMMERCIAL}
          component={OpenReferralsCommercialMain}
        />
        <LayoutRoute path={ROUTES.UNAUTHORIZED} component={Unauthorized} />
        <LayoutRoute path={ROUTES.END_SESSION} component={EndSession} />

        <LayoutRoute
          route={ROUTES.ADD_EDIT}
          path={ROUTES.ADD_EDIT}
          component={AddEditMain}
          layoutProps={{ noPadding: true }}
        />
        <SidebarLayoutRoute
          route={ROUTES.USER_MANAGEMENT}
          path={ROUTES.USER_MANAGEMENT}
          component={UserManagementMain}
          headerComponent={UserManagementHeader}
          layoutProps={{ hasSubHeader: true }}
        />

        <SidebarLayoutRoute
          route={ROUTES.CONFIGURATION}
          path={fullRoute(ROUTES.CONFIGURATION)}
          component={ConfigurationMain}
          headerComponent={ConfigurationHeader}
          layoutProps={{ hasSubHeader: true }}
        />

        <LayoutRoute
          route={ROUTES.ELMAH}
          path={ROUTES.ELMAH}
          component={ElmahErrors}
          exact
        />
        <LayoutRoute
          route={ROUTES.ELMAH}
          path={ROUTES.ELMAH + '/:id'}
          component={ElmahError}
          exact
        />
        <SidebarLayoutRoute
          route={ROUTES.EXAMPLES}
          path={ROUTES.EXAMPLES}
          component={ExamplesMain}
        />
        <LayoutRoute
          route={ROUTES.VIEW}
          path={ROUTES.VIEW}
          component={ViewMain}
          layoutProps={{ noPadding: true }}
        />
        {/*You would add your error route here because /* is a catch all route */}
        {/* <LayoutRoute path="/*" component={DashboardMain} /> */}
        <LayoutRoute
          route={ROUTES.REFERRAL}
          path={fullRoute(ROUTES.REFERRAL)}
          component={ReferralMain}
          providerComponent={EntityProvider}
          providerProps={{ getEntityUrl: ENDPOINTS.referrals.getInfoModel }}
          headerProps={{ hasTallHeader: false }}
        />
        <LayoutRoute
          route={ROUTES.REFERRALCOMMERCIAL}
          path={fullRoute(ROUTES.REFERRALCOMMERCIAL)}
          component={ReferralCommercialMain}
          providerComponent={EntityProvider}
          providerProps={{
            getEntityUrl: ENDPOINTS.referralsCommercial.getInfoModel,
          }}
          headerProps={{ hasTallHeader: false }}
        />
        <LayoutRoute
          route={ROUTES.REFERRALREQUESTPROPOSALS}
          path={ROUTES.REFERRALREQUESTPROPOSALS + '/:id'}
          component={ReferralsRequestProposalsMain}
          exact
        />
        <LayoutRoute
          route={ROUTES.REFERRALREQUESTPROPOSALSCOMMERCIAL}
          path={ROUTES.REFERRALREQUESTPROPOSALSCOMMERCIAL + '/:id'}
          component={ReferralsRequestProposaslCommercial}
          exact
        />

        <LayoutRoute
          route={ROUTES.CLAIMANT}
          path={fullRoute(ROUTES.CLAIMANT)}
          providerComponent={EntityProvider}
          providerProps={{ getEntityUrl: ENDPOINTS.claimants.getInfoModel }}
          component={ViewClaimantMain}
        />
        <LayoutRoute
          route={ROUTES.CLAIM}
          path={fullRoute(ROUTES.CLAIM)}
          component={ClaimMain}
          providerComponent={EntityProvider}
          providerProps={{ getEntityUrl: ENDPOINTS.claims.getInfoModel }}
          headerProps={{ hasTallHeader: false }}
        />
        <LayoutRoute
          route={ROUTES.REFERRALSREQUEST}
          path={fullRoute(ROUTES.REFERRALSREQUEST)}
          component={ReferralsRequestMain}
          providerComponent={EntityProvider}
          providerProps={{
            getEntityUrl: ENDPOINTS.referralsRequests.getInfoModel,
          }}
          headerProps={{ hasTallHeader: false }}
        />
        <LayoutRoute
          route={ROUTES.APPOINTMENT}
          path={fullRoute(ROUTES.APPOINTMENT)}
          component={AppointmentMain}
          providerComponent={EntityProvider}
          providerProps={{ getEntityUrl: ENDPOINTS.appointments.getInfoModel }}
          headerProps={{ hasTallHeader: false }}
        />
        <LayoutRoute
          route={ROUTES.REVIEWER_CONVERT}
          path={ROUTES.REVIEWER_CONVERT + '/:id'}
          component={ReviewerConvertMain}
          exact
        />
        <LayoutRoute
          route={ROUTES.ADD_AUDIT_REPORT}
          path={ROUTES.ADD_AUDIT_REPORT + '/:id'}
          component={AddAuditReport}
          exact
        />
        <LayoutRoute
          route={ROUTES.GUIDELINE}
          path={fullRoute(ROUTES.GUIDELINE)}
          providerComponent={EntityProvider}
          providerProps={{ getEntityUrl: ENDPOINTS.guidelines.getInfoModel }}
          component={ViewGuidelineMain}
        />
      </Switch>
    </Layout>
  );
};
